/** Token exchange rates: how many tokens per 1 unit of currency */
export const CURRENCY_EXCHANGE_RATES: Record<string, number> = {
  USD: 100, EUR: 100, GBP: 100, CAD: 100, AUD: 100,
  CHF: 100, CNY: 100, CRC: 1, INR: 1, JPY: 1,
}

/** FX rates: how many units of currency per 1 USD */
export const FX_RATES_TO_USD: Record<string, number> = {
  USD: 1.0, EUR: 0.92, GBP: 0.79, CAD: 1.36, AUD: 1.52,
  CHF: 0.88, CNY: 7.24, CRC: 500.0, INR: 83.0, JPY: 150.0,
}

/** Default exchange rate (tokens per 1 unit of currency) */
export const DEFAULT_EXCHANGE_RATE = 100

/**
 * Currency symbol mapping.
 *
 * MIRROR OF: `currencySymbols` in
 * server-golang/internal/services/subscription_notifications.go.
 * The Go test `TestCurrencySymbolTable_MirrorsSharedUtils` parses THIS literal
 * and fails if the two tables drift, so adding, removing, or re-glyphing an
 * entry here requires the same edit there. Keep the `XXX: 'sym',` shape \u2014 the
 * parser matches a 3-letter code followed by a single-quoted string.
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '\u20ac', GBP: '\u00a3', CAD: '$', AUD: '$',
  CHF: 'CHF', CNY: '\u00a5', CRC: '\u20a1', INR: '\u20b9', JPY: '\u00a5',
}

/** Groups an integer with commas: 1234567 -> "1,234,567". */
const groupThousands = (n: number): string => {
  const sign = n < 0 ? '-' : ''
  const digits = String(Math.abs(Math.trunc(n)))
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * Renders a minor-unit (cents) amount as the primary fiat display string.
 *
 * ONE RULE, TWO RUNTIMES. This is the TypeScript half of a formatting rule
 * mirrored by `formatFiatCents` in
 * server-golang/internal/services/subscription_notifications.go:
 *
 *   known code   -> "<symbol><comma-grouped integer>.<cc>"    e.g. "$1,234.50"
 *   unknown code -> "<ISO CODE> <comma-grouped integer>.<cc>"  e.g. "MXN 1,234.50"
 *
 * Use this \u2014 not `Intl.NumberFormat` \u2014 for any amount that ALSO appears in a
 * server-rendered notification about the same charge. Subscription billing is
 * the motivating case: the billing page, the pre-charge reminder email, and the
 * receipt all quote one number, and en-US `Intl` renders CAD as "CA$" and CRC
 * as "CRC", neither of which Go can reproduce without embedding CLDR data.
 * Reviewers caught exactly that divergence on #1429 (JPY: "\u00a510.00" on the page
 * vs "JPY 10.00" in the email).
 *
 * An empty/blank currency defaults to USD, matching the server's
 * `subscriptionCurrency` normalisation and the `COALESCE(default_currency,
 * 'USD')` in the due-subscriptions query.
 */
export const formatFiatCents = (cents: number, currency: string): string => {
  const safeCents = Number.isFinite(cents) ? Math.trunc(cents) : 0
  const code = (currency || '').trim().toUpperCase() || 'USD'

  const sign = safeCents < 0 ? '-' : ''
  const abs = Math.abs(safeCents)
  const amount = `${groupThousands(Math.floor(abs / 100))}.${String(abs % 100).padStart(2, '0')}`

  const symbol = CURRENCY_SYMBOLS[code]
  return symbol ? `${sign}${symbol}${amount}` : `${sign}${code} ${amount}`
}

/** Formats a major-unit amount for an editable numeric field. */
export const formatEditableFiatAmount = (amount: number): string => {
  if (!Number.isFinite(amount)) return ''

  return amount.toFixed(2).replace(/\.?0+$/, '')
}
